/**
 * In-browser fake backend that follows docs/api-contract.md, so the UI can be built and tested
 * before the FastAPI read endpoints exist. Also plays the WebSocket side (see realtime.ts).
 */
import type { ChatApi, UploadProgress } from './client'
import { ApiError } from './client'
import { countTabs, DEFAULT_SORT, inListTab, matchesChannel, matchesScope, scopeFor, SEGMENT_SORT, segmentOf, sortRows, toSummary, UNSENT_LABEL } from '../lib/inbox'
import {
  CANNED,
  createMockDb,
  MOCK_AI_CONFIG,
  MOCK_FOLLOW_UP_WINDOW_DAYS,
  SIM_NEW_CONTACTS,
  SIM_TEXTS,
  STAFF,
  type MockDb,
} from './mockData'
import {
  chatOnly,
  createMockCanned,
  createMockMedia,
  fillPlaceholders,
  MOCK_ANSWER_TYPES,
  MOCK_PLACEHOLDERS,
  unknownTokens,
} from './mockLibrary'
import { mockMenuSendMedia } from './kb/menuMock'
import type {
  AiConfig,
  AiDraftEvent,
  AiDraftResponse,
  AiIntent,
  AiSettings,
  Bootstrap,
  CannedInput,
  CannedItem,
  CannedList,
  ChatMessage,
  ConversationDetail,
  ConversationPage,
  ConversationPatch,
  ConversationSummary,
  DraftNextStep,
  LearnAction,
  LearnItem,
  ListQuery,
  MediaAsset,
  MediaList,
  MediaPatch,
  MediaQuery,
  MediaRegister,
  MediaUploadRequest,
  MediaUploadTicket,
  MessagePage,
  OrderDraft,
  RealtimeEvent,
  StaffAvailabilityEvent,
  SearchResult,
  TeamChangedEvent,
  SendMessageBody,
  SendResult,
  UploadTarget,
  WebchatSettings,
  WebchatSettingsResponse,
  WebOrders,
  WebVisitorInfo,
} from './types'
import { MOCK_WEBCHAT_SETTINGS, mockSettingsResponse, mockWebOrders, mockWebVisitors, questionsProblem } from './webchatMock'

/** Jev intents (api/app/ai/taxonomy.py) as GET /ai/intents returns them. */
const MOCK_INTENTS: AiIntent[] = [
  ['ship_fee', 'ค่าส่ง', 'shipping'], ['ship_date', 'วันได้ของ', 'shipping'], ['ship_area', 'พื้นที่ส่ง', 'shipping'],
  ['menu', 'เมนู', 'menu'], ['price', 'ราคา', 'menu'], ['order_link', 'ลิงก์สั่ง', 'menu'],
  ['storage', 'เก็บรักษา', 'care_status'], ['order_status', 'สถานะออเดอร์', 'care_status'],
  ['promo', 'โปรโมชั่น', 'blocked'], ['health', 'สุขภาพ', 'blocked'], ['allergy', 'แพ้อาหาร', 'blocked'],
  ['complaint', 'ร้องเรียน', 'blocked'], ['ordering', 'กำลังสั่ง', 'other'], ['payment', 'ชำระเงิน', 'other'],
  ['change_order', 'แก้ไขออเดอร์', 'other'], ['greeting_ack', 'ทักทาย/รับทราบ', 'other'], ['other', 'อื่นๆ', 'other'],
].map(([key, label, group]) => ({
  key: key as string,
  label: label as string,
  group: group as string,
  group_label: ({ shipping: 'ค่าส่ง · วันได้ของ · พื้นที่ส่ง', menu: 'เมนู · ราคา · ลิงก์สั่ง', care_status: 'เก็บรักษา · สถานะออเดอร์',
    blocked: 'เรื่องที่ต้องให้พนักงานตอบ', other: 'อื่นๆ' } as Record<string, string>)[group as string] ?? '',
  blocked: group === 'blocked',
  note: group === 'blocked' ? 'Jev ไม่เสนอคำตอบเรื่องนี้เอง — ใช้เป็นคำตอบแนะนำให้พนักงานตรวจแล้วกดส่งเท่านั้น' : null,
  script: MOCK_ANSWER_TYPES.some((t) => t.key === key),
})).concat(
  MOCK_ANSWER_TYPES.filter((t) => t.script).map((t) =>
    t.key.startsWith('pb_')
      ? {
          key: t.key, label: t.label, group: 'playbook', group_label: 'คู่มือปิดการขาย (ลูกค้าตอบกลับ)', blocked: false,
          note: 'ใช้ในกล่อง “แนะนำให้ตอบ” เมื่อลูกค้าตอบกลับแบบนี้ · คำตอบที่ไม่มีคำถาม ระบบต่อคำถามของขั้นถัดไปให้เอง (Jev ไม่เสนอ)',
          script: true,
        }
      : {
          key: t.key, label: t.label, group: 'script', group_label: 'คำแนะนำในแชท (ไม่ใช่คำถาม)', blocked: false,
          note: 'ใช้ในกล่อง “แนะนำให้ตอบ” เท่านั้น (ไม่ใช่คำถามของลูกค้า Jev ไม่เสนอ)', script: true,
        },
  ),
)

export interface MockOptions {
  /** Artificial latency per call (ms). 0 in tests. */
  latencyMs?: number
  /** How long POST …/draft "thinks" (ms). Default ~0.9 s with latency, 0 without (tests). */
  draftMs?: number
  clock?: () => number
  random?: () => number
}

const clone = <T>(v: T): T => structuredClone(v)

/** Mock only: a message containing this tag "fails on the platform" a moment later (to see the failed bubble). */
export const MOCK_FAIL_TAG = '#ส่งไม่สำเร็จ'

/**
 * Mock only (round 9 "กล่องร่าง"): this chat answers GET …/draft with a ready draft, as if the owner had turned
 * "ร่างอัตโนมัติทุกข้อความ" on — the look of the auto-ON case. Every other chat is `idle` until the staff ask.
 */
export const MOCK_AUTO_DRAFT_CHAT = 'c102'

/** Mock only: what POST …/draft writes for a chat without a hand-written AI block. */
/** The mock's "ให้ AI พาไปขั้นต่อไป" draft: an answer, then the pack step's question (one question). */
const NEXT_STEP_DRAFT =
  'ขอบคุณที่ถามนะคะ แอดมินขอเช็กรายละเอียดให้ก่อนนะคะ\n\nสนใจรับแพ็กไหนดีคะ ถ้ายังไม่แน่ใจ แอดมินช่วยแนะนำให้ได้นะคะ'
const GENERIC_DRAFT = 'สวัสดีค่ะ ขอบคุณที่ทักมานะคะ แอดมินขอเช็คข้อมูลให้สักครู่ แล้วจะรีบตอบกลับค่ะ'

export class MockChatApi implements ChatApi {
  readonly db: MockDb
  /**
   * Built its own demo data (no db passed in): the app's mock mode and screenshots. Demo-only extras (e.g. the
   * "สคริปต์แนะนำ" scripts of api/script/mock.ts) hang off this; tests pass their own db and get none of them.
   */
  readonly demo: boolean
  private readonly latency: number
  private readonly now: () => number
  private readonly random: () => number
  private listeners = new Set<(e: RealtimeEvent) => void>()
  private available = true
  /** พร้อมรับ switched off by the mock "server" after no activity (docs/routing.md). */
  private availableOff: { reason: 'idle'; at: string } | null = null
  private availListeners = new Set<(e: StaffAvailabilityEvent) => void>()
  /** team.changed / ai.draft on the same socket (realtime.ts connectMock forwards them). */
  private extraListeners = new Set<(e: TeamChangedEvent | AiDraftEvent) => void>()
  /** The owner's "AI ดูแลแชท" switch as bootstrap says it (screens flip it off for the empty AI tab). */
  aiModeEnabled = true
  /** client_id → message id: the same client_id returns the same message (like the API). */
  private sentByClient = new Map<string, string>()
  /** Tests: keep sends 'pending' until `settleSend()` (the real API confirms via message.updated). */
  deferSends = false
  /** Tests: the next POST …/messages is refused like the API does (e.g. 422 "เกิน 24 ชม. …"). */
  rejectNext: { status: number; detail: string } | null = null

  /** Library (docs/library.md): the shop's pictures/videos and answers. */
  media: MediaAsset[]
  canned: CannedItem[]
  /** Mock uploads: token → blob URL of what putMediaObject received. */
  private uploads = new Map<string, string>()
  /** Tests: the next upload PUT fails like the Worker would (e.g. 413). */
  failNextUpload: { status: number; detail: string } | null = null
  private aiConfig: AiConfig = structuredClone(MOCK_AI_CONFIG)
  /** Round 9: customer messages a draft was made for (`conv:message`) → run number. GET only reads these. */
  private drafted = new Map<string, number>()
  private readonly draftMs: number
  /** Tests: the next POST …/draft fails (e.g. a timeout) — the draft box shows a calm "ลองใหม่". */
  failNextDraft: { status: number; detail: string } | null = null

  constructor(db?: MockDb, opts: MockOptions = {}) {
    this.now = opts.clock ?? (() => Date.now())
    this.db = db ?? createMockDb(this.now())
    this.demo = !db
    this.latency = opts.latencyMs ?? 120
    this.draftMs = opts.draftMs ?? (this.latency > 0 ? 900 : 0)
    this.random = opts.random ?? Math.random
    this.media = createMockMedia(this.now())
    this.canned = createMockCanned(this.media, this.now())
    this.refreshUsedIn()
    // screenshots / demo: localStorage `cf-chat.mock-auto-off` = 1 → open as if พร้อมรับ was switched off 3 minutes ago
    try {
      if (localStorage.getItem('cf-chat.mock-auto-off') === '1') {
        this.available = false
        this.availableOff = { reason: 'idle', at: new Date(this.now() - 3 * 60000).toISOString() }
      }
    } catch {
      /* no storage (tests) */
    }
  }

  // ------------------------------------------------ พร้อมรับ auto-off (what api/app/routing/presence.py does)

  onAvailability(fn: (e: StaffAvailabilityEvent) => void): () => void {
    this.availListeners.add(fn)
    return () => this.availListeners.delete(fn)
  }

  /** The heartbeat reached the mock "server": no activity for auto_off_min → พร้อมรับ off + event. */
  heartbeat(msg: { idle_s?: unknown }): void {
    const idle = typeof msg.idle_s === 'number' ? msg.idle_s : 0
    const after = this.db.settings.auto_off_min ?? 15
    if (!this.available || after <= 0 || idle < after * 60) return
    const at = new Date(this.now()).toISOString()
    this.available = false
    this.availableOff = { reason: 'idle', at }
    const me = this.db.team.find((m) => m.id === this.db.meId)
    const e: StaffAvailabilityEvent = {
      type: 'staff.availability',
      staff: { id: this.db.meId, name: me?.name ?? '' },
      available: false,
      reason: 'idle',
      after_min: after,
      at,
    }
    for (const fn of this.availListeners) fn(clone(e))
  }

  // ------------------------------------------------ realtime side

  onExtra(fn: (e: TeamChangedEvent | AiDraftEvent) => void): () => void {
    this.extraListeners.add(fn)
    return () => this.extraListeners.delete(fn)
  }

  private emitExtra(e: TeamChangedEvent | AiDraftEvent): void {
    for (const fn of this.extraListeners) fn(clone(e))
  }

  /** The team changed (a member, cap, shift or routing setting — what the API announces as team.changed). */
  teamChanged(): void {
    this.emitExtra({ type: 'team.changed' })
  }

  /** Tests: the customer read our messages up to now — each unread outbound one comes back read (message.updated). */
  readByCustomer(convId: string): void {
    const c = this.conv(convId)
    c.contact_read_at = new Date(this.now()).toISOString()
    for (const m of this.db.threads.get(convId) ?? []) {
      if (m.kind !== 'message' || m.direction !== 'out' || m.read || m.send?.status === 'failed') continue
      m.read = true
      this.emit({ type: 'message.updated', conversation_id: convId, item: m, conversation: toSummary(c) })
    }
  }

  /** Our message went out now: the next ทักตาม round and its window (like api/app/inbox.py follow_up_due). */
  private followUpFrom(c: ConversationDetail, fromMs: number): void {
    const on = c.follow_up.enabled && !c.needs_reply
    c.follow_up_due_at = on ? new Date(fromMs + c.follow_up.after_minutes * 60000).toISOString() : null
    c.follow_up_until = on ? new Date(fromMs + MOCK_FOLLOW_UP_WINDOW_DAYS * 86400000).toISOString() : null
  }

  subscribe(fn: (e: RealtimeEvent) => void): () => void {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  private emit(e: RealtimeEvent): void {
    for (const fn of this.listeners) fn(clone(e))
  }

  /** Other mock backends on the same db (api/aimode/mock.ts) push their realtime events through here. */
  publish(e: RealtimeEvent): void {
    this.emit(e)
  }

  private async delay(): Promise<void> {
    if (this.latency > 0) await new Promise((r) => setTimeout(r, this.latency))
  }

  private conv(id: string): ConversationDetail {
    const c = this.db.conversations.get(id)
    if (!c) throw new ApiError(404, `conversation ${id} not found`)
    return c
  }

  private nextId(prefix: string): string {
    this.db.seq += 1
    return `${prefix}${this.db.seq}`
  }

  // ------------------------------------------------ reads

  async bootstrap(): Promise<Bootstrap> {
    await this.delay()
    const team = this.db.team.map((m) => ({
      ...m,
      available: m.id === this.db.meId ? this.available : m.available,
      available_off: m.id === this.db.meId ? this.availableOff : null,
      open_count: [...this.db.conversations.values()].filter(
        // like the API (SQL_LOADS, api/app/routing/engine.py): open chats waiting for our reply
        (c) => c.assigned_staff?.id === m.id && c.status === 'open' && c.needs_reply,
      ).length,
    }))
    const me = team.find((m) => m.id === this.db.meId)
    if (!me) throw new ApiError(500, 'me missing')
    return clone({
      me, team, queue_waiting: 0, settings: this.db.settings, canned_default: CANNED, ai_mode_enabled: this.aiModeEnabled,
    })
  }

  async listConversations(q: ListQuery): Promise<ConversationPage> {
    await this.delay()
    const now = this.now()
    // tag filter (team labels, api/app/collab): applies to the rows and the tab counts alike
    const all = [...this.db.conversations.values()].filter((c) => !q.tag || !!c.tags?.some((t) => t.id === q.tag))
    // like the API: กำลังคุย = ต้องตอบ then รอลูกค้าตอบ · the old keys needs_reply / waiting = one group each
    const inTab = all.filter(
      (c) =>
        inListTab(c, q.tab, now) && matchesChannel(c, q.channel) && matchesScope(c, scopeFor(q.tab, q.scope), this.db.meId),
    )
    const mode = q.tab === 'needs_reply' || q.tab === 'waiting' ? SEGMENT_SORT[q.tab] : DEFAULT_SORT[q.tab]
    const sorted = sortRows(inTab.map(toSummary), q.tab, mode)
    const limit = q.limit ?? 50
    const start = q.cursor ? Number(q.cursor) : 0
    const items = sorted.slice(start, start + limit)
    const next = start + limit < sorted.length ? String(start + limit) : null
    return clone({ items, next_cursor: next, counts: countTabs(all, now, q.channel, q.scope, this.db.meId) })
  }

  /** Like the API: the detail carries no AI block (`ai: null`); it comes from …/draft once a draft exists. */
  async getConversation(id: string): Promise<ConversationDetail> {
    await this.delay()
    return { ...clone(this.conv(id)), ai: null }
  }

  async listMessages(id: string, opts: { before?: string | null; limit?: number } = {}): Promise<MessagePage> {
    await this.delay()
    this.conv(id)
    const items = this.db.threads.get(id) ?? []
    const limit = opts.limit ?? 50
    const end = opts.before ? items.findIndex((m) => m.id === opts.before) : items.length
    const stop = end < 0 ? items.length : end
    const start = Math.max(0, stop - limit)
    return clone({ items: items.slice(start, stop), next_before: start > 0 ? (items[start]?.id ?? null) : null })
  }

  async search(q: string): Promise<SearchResult> {
    await this.delay()
    const needle = q.trim().toLowerCase()
    if (needle.length < 2) return { items: [] }
    const digits = needle.replace(/\D/g, '')
    const hits: SearchResult['items'] = []
    for (const c of this.db.conversations.values()) {
      if (c.contact.name.toLowerCase().includes(needle)) {
        hits.push({ conversation: toSummary(c), match: 'name', message: null })
        continue
      }
      if (digits.length >= 3 && (c.contact.phone_masked ?? '').replace(/\D/g, '').endsWith(digits)) {
        hits.push({ conversation: toSummary(c), match: 'phone', message: null })
        continue
      }
      const m = [...(this.db.threads.get(c.id) ?? [])]
        .reverse()
        .find((x): x is ChatMessage => x.kind === 'message' && (x.text ?? '').toLowerCase().includes(needle))
      if (m) {
        hits.push({
          conversation: toSummary(c),
          match: 'message',
          message: { id: m.id, text: m.text ?? '', at: m.at, direction: m.direction },
        })
      }
    }
    hits.sort((a, b) => (b.conversation.last_message_at ?? '').localeCompare(a.conversation.last_message_at ?? ''))
    return clone({ items: hits.slice(0, 30) })
  }

  // ------------------------------------------------ writes

  async markRead(id: string): Promise<void> {
    await this.delay()
    const c = this.conv(id)
    if (c.unread === 0) return
    c.unread = 0
    c.staff_read_at = new Date(this.now()).toISOString()
    this.emit({ type: 'conversation.updated', conversation: toSummary(c) })
  }

  async sendMessage(id: string, body: SendMessageBody): Promise<SendResult> {
    if (body.media_ids?.length) return this.sendWithMedia(id, body)
    await this.delay()
    const c = this.conv(id)
    const text = body.text.trim()
    if (!text) throw new ApiError(422, 'ข้อความว่าง')
    if (this.rejectNext) {
      const r = this.rejectNext
      this.rejectNext = null
      throw new ApiError(r.status, r.detail)
    }
    const known = this.sentByClient.get(`${id}:${body.client_id}`)
    const existing = known ? this.findMessage(id, known) : null
    if (existing) return clone({ message: existing, conversation: toSummary(c) })
    const nowIso = new Date(this.now()).toISOString()
    const me = STAFF[this.db.meId as keyof typeof STAFF] ?? STAFF.ploy
    const msg: ChatMessage = {
      kind: 'message',
      id: this.nextId('m'),
      conversation_id: id,
      direction: 'out',
      type: 'text',
      text,
      attachments: [],
      at: nowIso,
      author: { kind: 'staff', staff: { id: me.id, name: me.name } },
      read: false,
      send: {
        status: this.deferSends || text.includes(MOCK_FAIL_TAG) ? 'pending' : 'sent',
        error: null,
        client_id: body.client_id,
      },
    }
    if (text.includes(MOCK_FAIL_TAG)) {
      setTimeout(
        () => this.settleSend(id, msg.id, 'เกิน 24 ชม. หลังข้อความล่าสุดของลูกค้า ตอบใน Messenger ไม่ได้'),
        this.latency + 300,
      )
    }
    this.sentByClient.set(`${id}:${body.client_id}`, msg.id)
    this.recordUse(body)
    this.db.threads.get(id)?.push(msg)
    c.last_message = { text, type: 'text', direction: 'out', at: nowIso }
    c.last_message_at = nowIso
    c.needs_reply = false
    c.waiting_since = null
    c.unread = 0
    c.status = 'open'
    c.tag = c.tag?.kind === 'ok' ? null : c.tag
    this.followUpFrom(c, this.now())
    if (!c.assigned_staff) c.assigned_staff = { id: me.id, name: me.name }
    const conversation = toSummary(c)
    this.emit({ type: 'message.created', conversation_id: id, item: msg, conversation })
    return clone({ message: msg, conversation })
  }

  /** Text (optional) + one bubble per library picture, like the API (client ids "<id>:m<n>"). */
  private async sendWithMedia(id: string, body: SendMessageBody): Promise<SendResult> {
    await this.delay()
    const c = this.conv(id)
    const text = fillPlaceholders(body.text.trim(), c.contact.name)
    if (this.rejectNext) {
      const r = this.rejectNext
      this.rejectNext = null
      throw new ApiError(r.status, r.detail)
    }
    const known = this.sentByClient.get(`${id}:${body.client_id}`)
    if (known) {
      const all = (this.db.threads.get(id) ?? []).filter(
        (x): x is ChatMessage =>
          x.kind === 'message' && !!x.send?.client_id &&
          (x.send.client_id === body.client_id || x.send.client_id.startsWith(`${body.client_id}:m`)),
      )
      return clone({ message: all[0] as ChatMessage, messages: all, conversation: toSummary(c) })
    }
    const files = (body.media_ids ?? []).map((mid) => {
      const m = this.media.find((x) => x.id === mid && !x.deleted)
      if (!m) throw new ApiError(422, 'ไม่พบรูปหรือวิดีโอที่เลือก')
      return m
    })
    if (c.channel === 'line') {
      const webp = files.find((m) => m.mime === 'image/webp')
      if (webp) throw new ApiError(422, `“${webp.title}” เป็นรูป webp LINE ไม่แสดงรูปแบบนี้ ใช้รูป jpg หรือ png แทน`)
      if ((text ? 1 : 0) + files.length > 5) {
        throw new ApiError(422, 'LINE ส่งได้ครั้งละ 5 ข้อความ (ข้อความ 1 + รูปหรือวิดีโอ 4) เอารูปออกบางรายการ')
      }
    }
    const me = STAFF[this.db.meId as keyof typeof STAFF] ?? STAFF.ploy
    const nowIso = new Date(this.now()).toISOString()
    const make = (type: ChatMessage['type'], t: string | null, atts: ChatMessage['attachments'], cid: string): ChatMessage => ({
      kind: 'message', id: this.nextId('m'), conversation_id: id, direction: 'out', type, text: t, attachments: atts,
      at: nowIso, author: { kind: 'staff', staff: { id: me.id, name: me.name } }, read: false,
      send: { status: this.deferSends ? 'pending' : 'sent', error: null, client_id: cid },
    })
    const out: ChatMessage[] = []
    if (text) out.push(make('text', text, [], body.client_id))
    files.forEach((m, i) => {
      const cid = i === 0 && !text ? body.client_id : `${body.client_id}:m${i + 1}`
      out.push(make(m.kind, null, [{ type: m.kind, url: m.url, name: m.title, preview_url: m.preview_url, media_id: m.id,
        width: m.width, height: m.height }], cid))
      m.send_count += 1
      m.last_sent_at = nowIso
    })
    this.recordUse(body)
    this.sentByClient.set(`${id}:${body.client_id}`, out[0]?.id ?? '')
    const thread = this.db.threads.get(id)
    for (const m of out) thread?.push(m)
    const lastMsg = out[out.length - 1] as ChatMessage
    c.last_message = { text: lastMsg.type === 'text' ? (lastMsg.text ?? '') : '[รูปภาพ]', type: lastMsg.type, direction: 'out', at: nowIso }
    c.last_message_at = nowIso
    c.needs_reply = false
    c.waiting_since = null
    c.unread = 0
    c.status = 'open'
    this.followUpFrom(c, this.now())
    if (!c.assigned_staff) c.assigned_staff = { id: me.id, name: me.name }
    const conversation = toSummary(c)
    for (const m of out) this.emit({ type: 'message.created', conversation_id: id, item: m, conversation })
    return clone({ message: out[0] as ChatMessage, messages: out, conversation })
  }

  private recordUse(body: SendMessageBody): void {
    const r = this.canned.find((x) => x.id === body.canned_id)
    if (!r) return
    r.use_count += 1
    r.last_used_at = new Date(this.now()).toISOString()
  }

  private findMessage(convId: string, messageId: string): ChatMessage | null {
    const m = (this.db.threads.get(convId) ?? []).find((x) => x.id === messageId)
    return m && m.kind === 'message' ? m : null
  }

  /** Tests: finish a deferred send like the sender worker would (message.updated). */
  settleSend(convId: string, messageId: string, error: string | null = null): void {
    const m = this.findMessage(convId, messageId)
    if (!m?.send) return
    m.send = { ...m.send, status: error ? 'failed' : 'sent', error }
    this.emit({ type: 'message.updated', conversation_id: convId, item: m, conversation: toSummary(this.conv(convId)) })
  }

  /** Tests: the customer unsent a message (api/app/unsend.py) — content gone, placeholder via message.updated. */
  unsendMessage(convId: string, messageId: string): void {
    const m = this.findMessage(convId, messageId)
    if (!m) return
    Object.assign(m, { type: 'unsent', text: null, attachments: [], slip: null, tag: null })
    const c = this.conv(convId)
    const items = (this.db.threads.get(convId) ?? []).filter((x): x is ChatMessage => x.kind === 'message')
    if (items[items.length - 1]?.id === m.id) {
      c.last_message = { text: UNSENT_LABEL[m.direction], type: 'unsent', direction: m.direction, at: m.at }
    }
    this.emit({ type: 'message.updated', conversation_id: convId, item: m, conversation: toSummary(c) })
  }

  // ---- "/ln" card (api/app/ai/learn_routes.py): the screens click through its states
  private findLearn(messageId: string): [string, LearnItem] {
    for (const [convId, items] of this.db.threads) {
      const it = items.find((x): x is LearnItem => x.kind === 'learn' && x.id === messageId)
      if (it) return [convId, it]
    }
    throw new ApiError(404, 'ไม่พบคำสอนนี้')
  }

  private learnDone(convId: string, card: LearnItem): { item: LearnItem } {
    this.emit({ type: 'message.updated', conversation_id: convId, item: card, conversation: toSummary(this.conv(convId)) })
    return clone({ item: card })
  }

  async learnAction(messageId: string, action: LearnAction, text?: string): Promise<{ item: LearnItem }> {
    await this.delay()
    const [convId, card] = this.findLearn(messageId)
    const rule = card.rule
    if (!rule) throw new ApiError(409, 'คำสอนนี้ไม่มีกติกาให้อนุมัติ')
    if (rule.status !== 'pending_approval') throw new ApiError(409, 'ไม่มีอะไรรออนุมัติในข้อนี้')
    if (action === 'replace') {
      // kb.replace: the rules it contradicts stop being used (approved → ปิดใช้ · waiting → ไม่ใช้)
      for (const o of card.overlaps) {
        if (o.relation !== 'conflict') continue
        if (o.status === 'approved') o.status = 'retired'
        else if (o.status === 'pending_approval') o.status = 'rejected'
      }
    }
    if (action === 'approve' || action === 'replace') {
      if (text && text !== rule.text) Object.assign(rule, { text, edited: true, edited_by: 'เจ้าของ' })
      Object.assign(rule, { status: 'approved', approved_by: 'เจ้าของ', approved_at: new Date(this.now()).toISOString() })
    } else rule.status = 'rejected'
    return this.learnDone(convId, card)
  }

  async learnEdit(messageId: string, text: string): Promise<{ item: LearnItem }> {
    await this.delay()
    const [convId, card] = this.findLearn(messageId)
    if (!card.rule) throw new ApiError(409, 'คำสอนนี้ไม่มีกติกาให้แก้')
    Object.assign(card.rule, { text, edited: true, edited_by: 'เจ้าของ' })
    return this.learnDone(convId, card)
  }

  async retryMessage(messageId: string): Promise<SendResult> {
    await this.delay()
    for (const [convId, items] of this.db.threads) {
      const m = items.find((x): x is ChatMessage => x.kind === 'message' && x.id === messageId)
      if (!m) continue
      if (m.send?.status !== 'failed') throw new ApiError(409, 'ข้อความนี้ไม่ได้อยู่ในสถานะส่งไม่สำเร็จ')
      m.send = { ...m.send, status: this.deferSends ? 'pending' : 'sent', error: null }
      m.at = new Date(this.now()).toISOString()
      const conversation = toSummary(this.conv(convId))
      this.emit({ type: 'message.updated', conversation_id: convId, item: m, conversation })
      return clone({ message: m, conversation })
    }
    throw new ApiError(404, 'ไม่พบข้อความนี้')
  }

  async updateConversation(id: string, patch: ConversationPatch): Promise<ConversationSummary> {
    await this.delay()
    const c = this.conv(id)
    if (patch.status) c.status = patch.status
    if (patch.auto_close_off !== undefined) c.auto_close_off = patch.auto_close_off
    if (patch.follow_up_enabled !== undefined) {
      c.follow_up.enabled = patch.follow_up_enabled
      if (c.last_message_at) this.followUpFrom(c, Date.parse(c.last_message_at))
      else c.follow_up_due_at = c.follow_up_until = null
    }
    if (patch.assigned_staff_id) {
      const m = this.db.team.find((t) => t.id === patch.assigned_staff_id)
      if (!m) throw new ApiError(422, 'unknown staff')
      c.assigned_staff = { id: m.id, name: m.name }
    }
    const conversation = toSummary(c)
    if (patch.next_step && c.ai) {
      // like the API: the tick is saved with who/when and every screen gets ai.steps
      const me = STAFF[this.db.meId as keyof typeof STAFF] ?? STAFF.ploy
      const s = c.ai.next_steps.find((x) => x.id === patch.next_step?.id)
      if (s) {
        s.done = patch.next_step.done
        s.done_by = { id: me.id, name: me.name }
        s.done_at = new Date(this.now()).toISOString()
      }
      const steps = c.ai.next_steps
        .filter((x) => x.done_by)
        .map((x) => ({ id: x.id, done: x.done, by: x.done_by ?? null, at: x.done_at ?? null }))
      this.emit({ type: 'ai.steps', conversation_id: id, steps, conversation })
      return clone(conversation)
    }
    this.emit({ type: 'conversation.updated', conversation })
    return clone(conversation)
  }

  async setAvailability(available: boolean): Promise<void> {
    await this.delay()
    this.available = available
    this.availableOff = null
  }

  async getOrderDraft(id: string): Promise<OrderDraft | null> {
    await this.delay()
    this.conv(id)
    return clone(this.db.orderDrafts.get(id) ?? null)
  }

  // ------------------------------------------------ AI draft (round 9 "กล่องร่าง")

  /** The customer message a draft would answer now (the newest inbound one). */
  private lastInbound(id: string): ChatMessage | null {
    const hit = [...(this.db.threads.get(id) ?? [])].reverse().find((x) => x.kind === 'message' && x.direction === 'in')
    return (hit as ChatMessage | undefined) ?? null
  }

  /** The draft body for `run` (the hand-written AI block of the chat, or a generic polite line). */
  private draftBody(
    id: string,
    status: AiDraftResponse['status'],
    run: number | null,
    adapt = false,
    nextStep: DraftNextStep | null = null,
  ): AiDraftResponse {
    const c = this.conv(id)
    const msg = this.lastInbound(id)
    const base =
      status === 'ready'
        ? (c.ai ?? {
            close_chance: null,
            close_chance_note: 'ยังไม่พอประเมิน',
            next_steps: [],
            draft: GENERIC_DRAFT,
            canned: CANNED,
            cart: null,
            quick: null,
            draft_source: 'ai' as const,
          })
        : null
    // "ปรับตามแนวลูกค้า": the same demo draft, marked with the default style (the mock has no customer styles)
    const ai = base && adapt ? { ...base, adapt: { style: 'polite_short', label: 'สุภาพ กระชับ', returning: false, fallback: false } } : base
    // "ให้ AI พาไปขั้นต่อไป": a demo answer + one question toward the step (the API writes it from the chat's script)
    if (ai && nextStep) Object.assign(ai, { draft: NEXT_STEP_DRAFT, draft_source: 'ai', quick: null, next_step: nextStep })
    return clone({
      conversation_id: id,
      status,
      reason: null,
      run_id: status === 'ready' ? `mock-${id}-${msg?.id ?? 'x'}-${run ?? 1}` : null,
      for_message_id: msg?.id ?? null,
      generated_at: status === 'ready' ? new Date(this.now()).toISOString() : null,
      ai,
      classification: null,
      decision: null,
      error: null,
    })
  }

  /** Why there is no draft box at all (or a disabled one): like api/app/ai/routes.py `_unavailable`. */
  private draftUnavailable(id: string): AiDraftResponse | null {
    const c = this.conv(id)
    const msg = this.lastInbound(id)
    const reason = c.ai_mode ? 'ai_mode' : !msg ? 'no_inbound' : null
    if (!reason) return null
    return {
      conversation_id: id, status: 'unavailable', reason, run_id: null, for_message_id: null, generated_at: null,
      ai: null, classification: null, decision: null, error: null,
    }
  }

  /** GET = read only: the draft already made for the newest customer message, or `idle` (no AI work, no cost). */
  async getDraft(id: string): Promise<AiDraftResponse> {
    await this.delay()
    const off = this.draftUnavailable(id)
    if (off) return off
    const key = `${id}:${this.lastInbound(id)?.id ?? ''}`
    const run = this.drafted.get(key) ?? null
    const auto = !!this.aiConfig.settings.auto_draft || id === MOCK_AUTO_DRAFT_CHAT
    if (run === null && !auto) return this.draftBody(id, 'idle', null)
    return this.draftBody(id, 'ready', run)
  }

  /** POST = draft now (~0.9 s here): the existing draft of this message, or a new run with `refresh` (`suggestionId`,
   *  "เรียบเรียงใหม่" / "ปรับตามแนวลูกค้า" (`adapt`) of a script suggestion, gets the same demo draft here). */
  async requestDraft(
    id: string,
    opts: { refresh?: boolean; suggestionId?: string | null; adapt?: boolean; nextStep?: boolean } = {},
  ): Promise<AiDraftResponse> {
    await this.delay()
    if (this.draftMs > 0) await new Promise((r) => setTimeout(r, this.draftMs))
    if (this.failNextDraft) {
      const f = this.failNextDraft
      this.failNextDraft = null
      throw new ApiError(f.status, f.detail)
    }
    const off = this.draftUnavailable(id)
    if (off) return off
    const key = `${id}:${this.lastInbound(id)?.id ?? ''}`
    const prev = this.drafted.get(key)
    const run = prev === undefined ? 1 : opts.refresh ? prev + 1 : prev
    this.drafted.set(key, run)
    const next = opts.nextStep ? { step: 'pack', label: 'เลือกแพ็ก', pb_type: 'other', fallback: false } : null
    // like the API: other screens hear that this chat has a new draft (this one has the answer already)
    this.emitExtra({ type: 'ai.draft', conversation_id: id })
    return this.draftBody(id, 'ready', run, !!(opts.adapt && opts.suggestionId), next)
  }

  // ------------------------------------------------ library (docs/library.md)

  private refreshUsedIn(): void {
    for (const m of this.media) {
      m.used_in = this.canned
        .filter((r) => r.media.some((x) => x.id === m.id))
        .map((r) => ({ id: r.id, shortcut: r.shortcut, title: r.title }))
    }
  }

  async listMedia(q: MediaQuery = {}): Promise<MediaList> {
    await this.delay()
    this.refreshUsedIn()
    const live = this.media.filter((m) => !m.deleted)
    const needle = (q.q ?? '').trim().toLowerCase()
    const items = live
      .filter((m) => (q.folder === undefined || q.folder === null || m.folder === q.folder) && (!q.kind || m.kind === q.kind))
      .filter((m) => !needle || m.title.toLowerCase().includes(needle))
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
    const folders = [...new Set(live.map((m) => m.folder))].sort((a, b) => a.localeCompare(b, 'th'))
      .map((name) => ({ name, count: live.filter((m) => m.folder === name).length }))
    return clone({ items, folders, can_manage: true, enabled: true })
  }

  async createMediaUpload(req: MediaUploadRequest): Promise<MediaUploadTicket> {
    await this.delay()
    const kinds: Record<string, string> = { 'image/jpeg': 'image', 'image/png': 'image', 'image/webp': 'image', 'video/mp4': 'video' }
    if (kinds[req.mime] !== req.kind) throw new ApiError(422, 'ส่งได้เฉพาะรูป (jpg, png, webp) และวิดีโอ (mp4)')
    const max = req.kind === 'image' ? 10 * 1024 * 1024 : 200 * 1024 * 1024
    if (req.bytes > max) {
      throw new ApiError(422, `${req.kind === 'image' ? 'รูป' : 'วิดีโอ'}ใหญ่เกินที่ LINE รับได้ (${max / 1024 / 1024} MB) ลดขนาดก่อนอัปโหลด`)
    }
    const warnings: string[] = []
    if (req.mime === 'image/webp') warnings.push('LINE ไม่แสดงรูป webp ระบบจะส่งรูปนี้ได้เฉพาะ Messenger และ Instagram')
    if (req.kind === 'video' && req.bytes > 25 * 1024 * 1024) {
      warnings.push(`วิดีโอใหญ่ ${Math.round(req.bytes / 1024 / 1024)} MB ส่งได้เฉพาะ LINE (Messenger และ Instagram รับไม่เกิน 25 MB)`)
    }
    this.db.seq += 1
    const token = `mock${this.db.seq}tokenAAAAAAAAAAAAAAAA`
    return {
      token,
      duplicate: null,
      upload: { url: `mock://upload/${token}`, ticket: 'mock', multipart: req.bytes > 90 * 1024 * 1024, part_bytes: 50 * 1024 * 1024 },
      preview: req.preview ? { url: `mock://upload/${token}/p`, ticket: 'mock' } : null,
      expires_at: new Date(this.now() + 900_000).toISOString(),
      warnings,
    }
  }

  async putMediaObject(target: UploadTarget, blob: Blob, opts: { onProgress?: UploadProgress; signal?: AbortSignal } = {}): Promise<void> {
    const steps = this.latency > 0 ? 5 : 1
    for (let i = 1; i <= steps; i++) {
      if (opts.signal?.aborted) throw new ApiError(0, 'ยกเลิกการอัปโหลดแล้ว')
      if (this.latency > 0) await new Promise((r) => setTimeout(r, this.latency))
      opts.onProgress?.(Math.round((blob.size * i) / steps), blob.size)
    }
    if (this.failNextUpload) {
      const f = this.failNextUpload
      this.failNextUpload = null
      throw new ApiError(f.status, f.detail)
    }
    const key = target.url.replace('mock://upload/', '')
    let url: string
    try {
      url = URL.createObjectURL(blob)
    } catch {
      url = `blob:mock/${key}`
    }
    this.uploads.set(key, url)
  }

  async registerMedia(body: MediaRegister): Promise<MediaAsset> {
    await this.delay()
    const url = this.uploads.get(body.token)
    if (!url) throw new ApiError(409, 'อัปโหลดยังไม่เสร็จหรือไฟล์ไม่ครบ ลองอัปโหลดใหม่')
    const me = STAFF[this.db.meId as keyof typeof STAFF] ?? STAFF.ploy
    const asset: MediaAsset = {
      id: `md${++this.db.seq}`, kind: body.kind, title: body.title.trim(), folder: body.folder.trim(), url,
      preview_url: (body.has_preview && this.uploads.get(`${body.token}/p`)) || (body.kind === 'image' ? url : null),
      mime: body.mime, bytes: body.bytes, width: body.width, height: body.height, duration_ms: body.duration_ms,
      created_at: new Date(this.now()).toISOString(), created_by: me.id, created_by_name: me.name, used_in: [],
      send_count: 0, last_sent_at: null,
      limits: [
        ...(body.mime === 'image/webp' ? [{ channel: 'line' as const, reason: 'LINE ไม่แสดงรูป webp' }] : []),
        ...(body.kind === 'video' && body.bytes > 25 * 1024 * 1024
          ? [{ channel: 'fb' as const, reason: 'Messenger รับไม่เกิน 25 MB' }, { channel: 'ig' as const, reason: 'Instagram รับไม่เกิน 25 MB' }]
          : []),
      ],
      deleted: false,
    }
    this.media.unshift(asset)
    return clone(asset)
  }

  async updateMedia(id: string, patch: MediaPatch): Promise<MediaAsset> {
    await this.delay()
    const m = this.media.find((x) => x.id === id && !x.deleted)
    if (!m) throw new ApiError(404, 'ไม่พบไฟล์นี้')
    if (patch.title !== undefined) m.title = patch.title.trim().replace(/\s+/g, ' ')
    if (patch.folder !== undefined) m.folder = patch.folder.trim().replace(/\s+/g, ' ')
    for (const r of this.canned) r.media = r.media.map((x) => (x.id === id ? m : x))
    return clone(m)
  }

  async deleteMedia(id: string): Promise<void> {
    await this.delay()
    const m = this.media.find((x) => x.id === id && !x.deleted)
    if (!m) throw new ApiError(404, 'ไม่พบไฟล์นี้')
    m.deleted = true
    for (const r of this.canned) r.media = r.media.filter((x) => x.id !== id)
  }

  async listCanned(opts: { conversationId?: string | null } = {}): Promise<CannedList> {
    await this.delay()
    const name = opts.conversationId ? this.db.conversations.get(opts.conversationId)?.contact.name ?? null : null
    const items = [...this.canned]
      .sort((a, b) => Number(b.is_active) - Number(a.is_active) || a.sort - b.sort || b.use_count - a.use_count)
      .map((r) => ({ ...r, text: fillPlaceholders(r.body, name) }))
    // /menu sends the poster of the approved "เมนูรอบนี้" while that round is in use (mock switch cf-chat.menu-mock)
    const poster = mockMenuSendMedia(this.media)
    for (const r of items) r.send_media = r.shortcut === 'menu' ? poster : null
    const answerTypes = MOCK_ANSWER_TYPES.map((t) => {
      const mine = this.canned.filter((r) => r.intents.includes(t.key))
      return { ...t, active: mine.filter((r) => r.is_active).length, drafts: mine.filter((r) => r.is_draft && !r.is_active).map((r) => r.id) }
    })
    return clone({ items, placeholders: MOCK_PLACEHOLDERS, can_manage: true, answer_types: answerTypes })
  }

  private checkCanned(body: CannedInput, selfId: string | null): void {
    if (body.shortcut !== undefined) {
      const sc = body.shortcut.trim().replace(/^\/+/, '').toLowerCase()
      if (!/^[^\s/]{1,32}$/.test(sc)) throw new ApiError(422, 'คำสั่งลัดต้องเป็นคำเดียว ไม่มีช่องว่างหรือ / ยาวไม่เกิน 32 ตัว')
      if (this.canned.some((r) => r.shortcut === sc && r.id !== selfId)) throw new ApiError(409, `มีคำสั่ง /${sc} อยู่แล้ว ใช้คำอื่น`)
      body.shortcut = sc
    }
    for (const k of body.intents ?? []) {
      const it = MOCK_INTENTS.find((x) => x.key === k)
      if (!it) throw new ApiError(422, 'หัวข้อคำถามไม่ถูกต้อง')
    }
    const bad = unknownTokens(body.body ?? '')
    if (bad.length) throw new ApiError(422, `ไม่รู้จักตัวแปร ${bad.join(' ')} — ใช้ปุ่ม “ใส่ข้อมูลอัตโนมัติ” หรือเอาวงเล็บปีกกาออก`)
    if ((body.media_ids?.length ?? 0) > 4) {
      throw new ApiError(422, 'แนบรูปหรือวิดีโอได้ไม่เกิน 4 รายการต่อคำตอบ (LINE ส่งได้ครั้งละ 5 ข้อความ รวมข้อความ 1)')
    }
  }

  private mediaOf(ids: string[] | undefined, fallback: MediaAsset[]): MediaAsset[] {
    if (!ids) return fallback
    return ids.map((id) => {
      const m = this.media.find((x) => x.id === id && !x.deleted)
      if (!m) throw new ApiError(422, 'ไม่พบรูปหรือวิดีโอที่เลือก')
      return m
    })
  }

  async createCanned(body: CannedInput): Promise<CannedItem> {
    await this.delay()
    if (!body.shortcut || !body.title?.trim()) throw new ApiError(422, 'ใส่คำสั่งลัดและชื่อคำตอบ')
    this.checkCanned(body, null)
    const media = this.mediaOf(body.media_ids, [])
    if (!(body.body ?? '').trim() && !media.length) throw new ApiError(422, 'ใส่ข้อความ หรือแนบรูป/วิดีโออย่างน้อย 1 รายการ')
    const item: CannedItem = {
      id: `cr${++this.db.seq}`, shortcut: body.shortcut, title: body.title.trim(), body: (body.body ?? '').trim(),
      text: fillPlaceholders((body.body ?? '').trim(), null), intents: body.intents ?? [], media,
      is_active: body.is_active ?? true, is_draft: false, chat_only: chatOnly(body.body ?? ''), sort: Math.max(0, ...this.canned.map((r) => r.sort)) + 10, use_count: 0,
      last_used_at: null, updated_at: new Date(this.now()).toISOString(), updated_by_name: 'พลอย',
    }
    this.canned.push(item)
    return clone(item)
  }

  async updateCanned(id: string, patch: CannedInput): Promise<CannedItem> {
    await this.delay()
    const r = this.canned.find((x) => x.id === id)
    if (!r) throw new ApiError(404, 'ไม่พบคำตอบนี้')
    this.checkCanned(patch, id)
    const media = this.mediaOf(patch.media_ids, r.media)
    const body = patch.body !== undefined ? patch.body.trim() : r.body
    if (!body && !media.length) throw new ApiError(422, 'ใส่ข้อความ หรือแนบรูป/วิดีโออย่างน้อย 1 รายการ')
    Object.assign(r, {
      shortcut: patch.shortcut ?? r.shortcut, title: patch.title?.trim() || r.title, body, text: fillPlaceholders(body, null),
      intents: patch.intents ?? r.intents, is_active: patch.is_active ?? r.is_active, media,
      is_draft: false, chat_only: chatOnly(body),
      updated_at: new Date(this.now()).toISOString(), updated_by_name: 'พลอย',
    })
    return clone(r)
  }

  async deleteCanned(id: string): Promise<void> {
    await this.delay()
    const i = this.canned.findIndex((x) => x.id === id)
    if (i < 0) throw new ApiError(404, 'ไม่พบคำตอบนี้')
    this.canned.splice(i, 1)
  }

  async reorderCanned(ids: string[]): Promise<void> {
    await this.delay()
    ids.forEach((id, i) => {
      const r = this.canned.find((x) => x.id === id)
      if (r) r.sort = (i + 1) * 10
    })
  }

  async listIntents(): Promise<AiIntent[]> {
    await this.delay()
    return clone(MOCK_INTENTS)
  }

  /** Mock pictures never expire: remember the ask so tests can see it, nothing else to do. */
  readonly refreshedProfiles: string[] = []
  async refreshProfile(contactId: string): Promise<void> {
    await this.delay()
    this.refreshedProfiles.push(contactId)
  }

  // ------------------------------------------------ AI settings page + slip check


  async getAiConfig(): Promise<AiConfig> {
    await this.delay()
    return clone(this.aiConfig)
  }

  async updateAiSettings(patch: Partial<AiSettings>): Promise<AiSettings> {
    await this.delay()
    if (patch.threshold !== undefined && (patch.threshold < 0.5 || patch.threshold > 1)) throw new ApiError(422, 'threshold')
    this.aiConfig.settings = { ...this.aiConfig.settings, ...clone(patch) }
    return clone(this.aiConfig.settings)
  }

  async updateSlipSettings(patch: { auto: boolean }): Promise<{ auto: boolean }> {
    await this.delay()
    this.aiConfig.slip.auto = patch.auto
    return { auto: patch.auto }
  }

  /** Like the API: a final result is returned as is (no second charge). Mock: a picture named "…สลิป…" passes,
   *  anything else is "ไม่ใช่สลิป". */
  async checkSlip(messageId: string): Promise<SendResult> {
    await this.delay()
    for (const [convId, items] of this.db.threads) {
      const m = items.find((x): x is ChatMessage => x.kind === 'message' && x.id === messageId)
      if (!m) continue
      if (m.direction !== 'in' || m.type !== 'image') throw new ApiError(422, 'ตรวจสลิปได้เฉพาะรูปที่ลูกค้าส่งมา')
      if (!m.slip || m.slip.can_retry) {
        await new Promise((r) => setTimeout(r, this.latency * 4))
        const nowIso = new Date(this.now()).toISOString()
        const looksLikeSlip = m.attachments.some((a) => /สลิป/.test(a.name ?? ''))
        m.slip = looksLikeSlip
          ? {
              status: 'ok',
              label: 'สลิปผ่าน',
              amount: 2255,
              bank: 'SCB',
              slip_at: new Date(this.now() - 3 * 60000).toISOString(),
              trans_ref: '016269140211ABC',
              dup: null,
              note: null,
              via: 'staff',
              checked_at: nowIso,
              can_retry: false,
            }
          : {
              status: 'not_slip',
              label: 'ไม่ใช่สลิป',
              amount: null,
              bank: null,
              slip_at: null,
              trans_ref: null,
              dup: null,
              note: 'อ่านสลิปในรูปไม่ได้ (ไม่ใช่สลิป หรือรูปไม่ชัด)',
              via: 'staff',
              checked_at: nowIso,
              can_retry: false,
            }
        this.emit({ type: 'message.updated', conversation_id: convId, item: m, conversation: toSummary(this.conv(convId)) })
      }
      return clone({ message: m, conversation: toSummary(this.conv(convId)) })
    }
    throw new ApiError(404, 'ไม่พบข้อความนี้')
  }

  // ------------------------------------------------ web chat

  private webVisitors: Map<string, WebVisitorInfo> | null = null
  private webchatSettings: WebchatSettings = { ...MOCK_WEBCHAT_SETTINGS }
  /** Tests: conversation ids the staff typed in (POST …/typing). */
  typingPings: string[] = []

  async getWebVisitor(id: string): Promise<WebVisitorInfo | null> {
    await this.delay()
    if (this.conv(id).channel !== 'web') return null
    this.webVisitors ??= mockWebVisitors(this.now())
    const v = this.webVisitors.get(id)
    if (v) return clone(v)
    const c = this.conv(id)
    return {
      site: 'cleanfitdelivery.com', name: null, phone: null,
      current_page: { url: 'https://cleanfitdelivery.com/order', title: 'สั่งอาหาร' },
      first_page: 'https://cleanfitdelivery.com/order', referrer: null, utm: null, last_utm: null,
      device: 'mobile', identified: false, first_seen_at: c.contact.first_seen_at, last_seen_at: c.contact.last_seen_at,
    }
  }

  /** Tests: how often the card asked for POS orders (fresh = "โหลดใหม่"). */
  webOrderCalls: { id: string; fresh: boolean }[] = []

  async getWebOrders(id: string, fresh = false): Promise<WebOrders | null> {
    await this.delay()
    if (this.conv(id).channel !== 'web') return null
    this.webOrderCalls.push({ id, fresh })
    this.webVisitors ??= mockWebVisitors(this.now())
    return clone(mockWebOrders(id, this.webVisitors.get(id) ?? null, this.now()))
  }

  async sendTyping(id: string): Promise<void> {
    this.conv(id)
    this.typingPings.push(id)
  }

  async getWebchatSettings(): Promise<WebchatSettingsResponse> {
    await this.delay()
    return mockSettingsResponse(this.webchatSettings)
  }

  async updateWebchatSettings(patch: Partial<WebchatSettings>): Promise<WebchatSettingsResponse> {
    await this.delay()
    const next = { ...this.webchatSettings, ...patch }
    if (!next.offline_text.trim()) throw new ApiError(422, 'กรุณาใส่ข้อความนอกเวลาทำการ')
    if (next.open_time === next.close_time) throw new ApiError(422, 'เวลาเปิดและเวลาปิดต้องไม่ตรงกัน')
    if (next.hours_mode === 'set' && !next.open_days.length) throw new ApiError(422, 'เลือกวันทำการอย่างน้อย 1 วัน')
    const problem = questionsProblem(next.questions)
    if (problem) throw new ApiError(422, problem)
    next.questions = next.questions.map((q) => ({ ...q, text: q.text.trim().replace(/\s+/g, ' '),
      pages: q.pages.map((p) => (p.length > 1 ? p.trim().replace(/\/+$/, '') : p.trim())).filter(Boolean) }))
    this.webchatSettings = next
    return mockSettingsResponse(next)
  }

  // ------------------------------------------------ simulator

  /**
   * A customer writes: mostly to a chat that is waiting on them, sometimes a brand-new contact.
   * Emits `message.created` exactly like the WebSocket would.
   */
  simulateInbound(): string {
    const now = this.now()
    const nowIso = new Date(now).toISOString()
    const pick = <T>(xs: readonly T[]): T => xs[Math.floor(this.random() * xs.length) % xs.length] as T
    const waiting = [...this.db.conversations.values()].filter((c) => segmentOf(c, now) === 'waiting')
    let c: ConversationDetail
    if (waiting.length > 0 && this.random() < 0.75) {
      c = pick(waiting)
    } else {
      const [name, channel] = pick(SIM_NEW_CONTACTS)
      const id = this.nextId('c')
      const staff = pick(this.db.team.filter((t) => t.available))
      c = {
        id,
        channel,
        kind: 'dm',
        status: 'open',
        contact: {
          id: `ct-${id}`,
          name,
          initial: name.replace(/^คุณ/, '')[0] ?? '?',
          avatar_url: null,
          channel,
          phone_masked: null,
          first_seen_at: nowIso,
          last_seen_at: nowIso,
        },
        last_message: null,
        last_message_at: null,
        needs_reply: false,
        waiting_since: null,
        follow_up_due_at: null,
        unread: 0,
        assigned_staff: { id: staff.id, name: staff.name },
        tag: null,
        hot: false,
        account: { id: `acc-${channel}`, channel, name: 'Cleanfit' },
        pos: null,
        ad_source: null,
        auto_close_off: false,
        follow_up: { enabled: true, after_minutes: this.db.settings.follow_up_after_min },
        staff_read_at: null,
        contact_read_at: null,
        ai: null,
      }
      this.db.conversations.set(id, c)
      this.db.threads.set(id, [])
    }
    const text = pick(SIM_TEXTS)
    const msg: ChatMessage = {
      kind: 'message',
      id: this.nextId('m'),
      conversation_id: c.id,
      direction: 'in',
      type: 'text',
      text,
      attachments: [],
      at: nowIso,
      author: { kind: 'contact' },
      read: false,
    }
    this.db.threads.get(c.id)?.push(msg)
    c.last_message = { text, type: 'text', direction: 'in', at: nowIso }
    c.last_message_at = nowIso
    c.waiting_since = c.needs_reply && c.waiting_since ? c.waiting_since : nowIso
    c.needs_reply = true
    c.status = 'open'
    c.unread += 1
    c.follow_up_due_at = null
    c.follow_up_until = null
    c.contact.last_seen_at = nowIso
    this.emit({ type: 'message.created', conversation_id: c.id, item: msg, conversation: toSummary(c) })
    return c.id
  }
}
